import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  ArrowLeft, 
  Search,
  Package,
  FileText,
  Users
} from 'lucide-react';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

const SearchResultsPage: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<any>({ products: [], invoices: [], projects: [] });
  const [query, setQuery] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    setQuery(q);
    if (!q) {
      setLoading(false);
      setResults({ products: [], invoices: [], projects: [] });
      return;
    }
    setLoading(true);
    Promise.all([
      apiService.get(`/products?search=${encodeURIComponent(q)}`),
      apiService.get(`/invoices?search=${encodeURIComponent(q)}`),
      apiService.get(`/projects?search=${encodeURIComponent(q)}`),
    ]).then(([productsRes, invoicesRes, projectsRes]) => {
      setResults({
        products: productsRes.data.products || [],
        invoices: invoicesRes.data.invoices || [],
        projects: projectsRes.data.projects || [],
      });
      setLoading(false);
    }).catch(() => {
      setResults({ products: [], invoices: [], projects: [] });
      setLoading(false);
    });
  }, [location.search]);

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Search Results for "{query}"</h1>
      {results.products.length === 0 && results.invoices.length === 0 && results.projects.length === 0 ? (
        <div className="text-gray-500">No results found.</div>
      ) : (
        <>
          {results.products.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Products</h2>
              <ul className="list-disc pl-6">
                {results.products.map((p: any) => (
                  <li key={p._id}>{p.name} (SKU: {p.sku})</li>
                ))}
              </ul>
            </div>
          )}
          {results.invoices.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Invoices</h2>
              <ul className="list-disc pl-6">
                {results.invoices.map((inv: any) => (
                  <li key={inv._id}>Invoice #{inv.invoiceNumber} ({inv.type})</li>
                ))}
              </ul>
            </div>
          )}
          {results.projects.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-2">Projects</h2>
              <ul className="list-disc pl-6">
                {results.projects.map((proj: any) => (
                  <li key={proj._id}>{proj.name}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const NotFoundPage: React.FC = () => {
  const { user } = useAuth();
  
  const quickLinks = [
    { name: 'Dashboard', icon: <Home className="w-5 h-5" />, path: user ? '/dashboard' : '/login' },
    { name: 'Products', icon: <Package className="w-5 h-5" />, path: user ? '/products' : '/login' },
    { name: 'Invoices', icon: <FileText className="w-5 h-5" />, path: user ? '/invoices' : '/login' },
    { name: 'Users', icon: <Users className="w-5 h-5" />, path: user ? '/users' : '/login' },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 } as any}
          animate={{ opacity: 1, y: 0 } as any}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          {/* 404 Number */}
          <motion.div
            initial={{ scale: 0 } as any}
            animate={{ scale: 1 } as any}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="text-9xl font-bold text-[var(--color-primary)] mb-4 font-['Times_New_Roman']"
          >
            404
          </motion.div>

          {/* Error Message */}
          <motion.h1
            initial={{ opacity: 0 } as any}
            animate={{ opacity: 1 } as any}
            transition={{ delay: 0.4 }}
            className="text-3xl font-bold text-[var(--color-text)] font-['Times_New_Roman']"
          >
            Page Not Found
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 } as any}
            animate={{ opacity: 1 } as any}
            transition={{ delay: 0.6 }}
            className="text-lg text-[var(--color-text-secondary)] mb-8 font-['Times_New_Roman']"
          >
            Sorry, the page you're looking for doesn't exist or has been moved.
          </motion.p>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 } as any}
            animate={{ opacity: 1, y: 0 } as any}
            transition={{ delay: 0.8 }}
            className="max-w-md mx-auto mb-8"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search for pages..."
                className="w-full pl-10 pr-4 py-3 bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman']"
              />
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 } as any}
            animate={{ opacity: 1, y: 0 } as any}
            transition={{ delay: 1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <Link
              to={user ? "/dashboard" : "/login"}
              className="flex items-center justify-center px-6 py-3 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
            >
              <Home className="w-5 h-5 mr-2" />
              {user ? "Go to Dashboard" : "Go to Login"}
            </Link>
            <button
              onClick={() => window.history.back()}
              className="flex items-center justify-center px-6 py-3 bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors font-['Times_New_Roman']"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Go Back
            </button>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0 } as any}
            animate={{ opacity: 1 } as any}
            transition={{ delay: 1.2 }}
            className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-md p-6"
          >
            <h3 className="text-lg font-semibold text-[var(--color-text)] mb-4 font-['Times_New_Roman']">
              Quick Navigation
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, scale: 0.8 } as any}
                  animate={{ opacity: 1, scale: 1 } as any}
                  transition={{ delay: 1.4 + index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className="flex flex-col items-center p-4 bg-[var(--color-background)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors group border border-[var(--color-border)]"
                  >
                    <div className="text-[var(--color-primary)] mb-2">
                      {link.icon}
                    </div>
                    <span className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                      {link.name}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0 } as any}
            animate={{ opacity: 1 } as any}
            transition={{ delay: 1.6 }}
            className="mt-8 text-center"
          >
            <p className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              The Aircons Ltd. - Inventory Management System
            </p>
            <p className="text-xs text-[var(--color-text-secondary)] opacity-70 mt-1 font-['Times_New_Roman']">
              If you believe this is an error, please contact your system administrator.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFoundPage; 